import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import {
  createNewTppZip,
  getLastTppZipFileName,
  getTppLocalApiError,
  isTppFsaaSupported,
  openExistingTppZip,
  peekStoredTppZipResume,
  resumeLastTppZip,
  tryRestoreTppZipSession,
} from '../../../services/transitPassPool.service';
import {
  LocalZipFileIdentity,
  LocalZipRecentList,
} from '../../common/localZip/LocalZipFileIdentity';
import {
  listLocalZipRecent,
  migrateLegacyLastZipFileName,
  type LocalZipRecentEntry,
} from '../../../utils/localZip/localZipRecentStore';
import './TppZipGate.css';

interface TppZipGateProps {
  onConnected: () => void;
  onError: (msg: string) => void;
}

type ZipGateAction = 'continue' | 'create' | 'open' | null;

function TppZipGateChoice({
  action,
  busyAction,
  busy,
  primary,
  mutedBadge,
  iconClass,
  label,
  desc,
  className = '',
  onClick,
}: {
  action: Exclude<ZipGateAction, null>;
  busyAction: ZipGateAction;
  busy: boolean;
  primary?: boolean;
  mutedBadge?: boolean;
  iconClass: string;
  label: ReactNode;
  desc: ReactNode;
  className?: string;
  onClick: () => void;
}) {
  const active = busyAction === action;
  return (
    <button
      type="button"
      className={`tpp-gate-choice${primary ? ' tpp-gate-choice--primary' : ''}${className ? ` ${className}` : ''}`}
      disabled={busy}
      aria-busy={active}
      onClick={onClick}
    >
      <span
        className={`tpp-gate-choice__badge${mutedBadge ? ' tpp-gate-choice__badge--muted' : ''}`}
        aria-hidden
      >
        {active ? <Spinner size="sm" animation="border" /> : <i className={iconClass} />}
      </span>
      <span className="tpp-gate-choice__label">{label}</span>
      <span className="tpp-gate-choice__desc">{desc}</span>
    </button>
  );
}

function resolvePreviousEntry(
  resumeFileName: string | null,
  lastZipName: string | null,
  recent: LocalZipRecentEntry[]
): LocalZipRecentEntry | null {
  if (resumeFileName) {
    return (
      recent.find((e) => e.name === resumeFileName) ?? {
        name: resumeFileName,
        path: null,
        updatedAt: '',
      }
    );
  }
  if (recent[0]) return recent[0];
  if (lastZipName) return { name: lastZipName, path: null, updatedAt: '' };
  return null;
}

export default function TppZipGate({ onConnected, onError }: TppZipGateProps) {
  const { t } = useTranslation();
  const onConnectedRef = useRef(onConnected);
  onConnectedRef.current = onConnected;
  const [busyAction, setBusyAction] = useState<ZipGateAction>(null);
  const [bootstrapping, setBootstrapping] = useState(true);
  /** IndexedDB に保持した File System Access ハンドル（ワンクリック再開可） */
  const [resumeFileName, setResumeFileName] = useState<string | null>(null);
  const [lastZipName] = useState(() => getLastTppZipFileName());
  const [recentEntries] = useState(() => {
    migrateLegacyLastZipFileName('tpp', 'tpp.lastZipFileName');
    return listLocalZipRecent('tpp');
  });
  const fsaa = isTppFsaaSupported();
  const busy = busyAction != null;
  const canSilentResume = !!resumeFileName;
  const previousEntry = resolvePreviousEntry(resumeFileName, lastZipName, recentEntries);
  const previousName = previousEntry?.name ?? null;
  /** 前回利用あり → 「続き」を独立カードで出す（ハンドル有無は desc / 挙動だけ変える） */
  const showContinue = !!previousName;

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      // FSAA / IndexedDB が固まる環境でもゲートを無限待ちにしない
      const result = await Promise.race([
        tryRestoreTppZipSession(),
        new Promise<'none'>((resolve) => {
          window.setTimeout(() => resolve('none'), 2500);
        }),
      ]);
      if (cancelled) return;
      if (result === 'restored') {
        onConnectedRef.current();
        setBootstrapping(false);
        return;
      }
      const peek = await Promise.race([
        peekStoredTppZipResume(),
        new Promise<null>((resolve) => {
          window.setTimeout(() => resolve(null), 1500);
        }),
      ]);
      if (!cancelled) {
        setResumeFileName(peek?.fileName ?? null);
        setBootstrapping(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const run = async (action: Exclude<ZipGateAction, null>, fn: () => Promise<void>) => {
    setBusyAction(action);
    try {
      await fn();
      onConnected();
    } catch (e) {
      const msg = getTppLocalApiError(e);
      if ((e as Error).message !== 'zip_pick_cancelled' && msg) {
        onError(msg);
      }
      if ((e as Error).message === 'zip_no_stored_handle') {
        setResumeFileName(null);
      }
    } finally {
      setBusyAction(null);
    }
  };

  const onContinue = () => {
    if (canSilentResume) {
      void run('continue', resumeLastTppZip);
      return;
    }
    // ファイル名だけ覚えている場合はピッカーで同じ Zip を選んでもらう
    void run('continue', openExistingTppZip);
  };

  if (bootstrapping) {
    return (
      <section className="tpp-gate tpp-gate--booting" aria-busy="true" aria-live="polite">
        <div className="d-flex align-items-center gap-2 text-muted small">
          <Spinner size="sm" animation="border" />
          {t('tpp.zip_gate_restoring')}
        </div>
      </section>
    );
  }

  return (
    <section className="tpp-gate" aria-labelledby="tpp-zip-gate-title">
      <h2 id="tpp-zip-gate-title" className="tpp-gate__title">
        {t('tpp.zip_gate_title')}
      </h2>
      <p className="tpp-gate__privacy">
        {t('tpp.zip_gate_privacy')}
        {fsaa ? ` ${t('tpp.zip_gate_save_hint')}` : ` ${t('tpp.zip_gate_fsaa_hint')}`}
      </p>

      {recentEntries.length > 0 ? (
        <LocalZipRecentList
          entries={recentEntries}
          heading={t('common.local_zip_recent_heading', {
            defaultValue: '最近使った Zip（パス付き）',
          })}
        />
      ) : null}

      <div className={`tpp-gate__choices${showContinue ? ' tpp-gate__choices--with-continue' : ''}`}>
        {showContinue && previousEntry && (
          <TppZipGateChoice
            action="continue"
            busyAction={busyAction}
            busy={busy}
            primary
            className="tpp-gate-choice--continue"
            iconClass="fas fa-history"
            label={
              <LocalZipFileIdentity
                name={previousEntry.name}
                path={previousEntry.path}
                hint={t('common.local_zip_continue_hint', { defaultValue: '前回の続き' })}
              />
            }
            desc={
              canSilentResume
                ? t('tpp.zip_gate_continue_desc_resume')
                : t('tpp.zip_gate_continue_desc_pick')
            }
            onClick={onContinue}
          />
        )}

        <TppZipGateChoice
          action="open"
          busyAction={busyAction}
          busy={busy}
          primary={!showContinue}
          mutedBadge={showContinue}
          iconClass="fas fa-folder-open"
          label={showContinue ? t('tpp.zip_gate_open_other') : t('tpp.zip_gate_open')}
          desc={t('tpp.zip_gate_step_existing')}
          onClick={() => void run('open', openExistingTppZip)}
        />

        <TppZipGateChoice
          action="create"
          busyAction={busyAction}
          busy={busy}
          mutedBadge
          iconClass="fas fa-plus"
          label={t('tpp.zip_gate_create')}
          desc={t('tpp.zip_gate_step_new')}
          onClick={() => void run('create', createNewTppZip)}
        />
      </div>

      {showContinue && previousName ? (
        <p className="tpp-gate__note">
          {canSilentResume
            ? t('tpp.zip_gate_note_resume')
            : t('tpp.zip_gate_note_continue_pick', { name: previousName })}
        </p>
      ) : (
        <p className="tpp-gate__note">{t('tpp.zip_gate_note')}</p>
      )}
    </section>
  );
}
